import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, X } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';
import { markDashboardNotificationRead } from '../api/dashboard';
import { formatDateTime } from '../lib/formatters';
import { getNotificationTargetUrl } from '../lib/notificationTargets';

export default function NotificationDrawer({ onClose }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await axiosInstance.get('/api/dashboard/notifications', {
          withCredentials: true,
        });
        setNotifications(res.data);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  // Mark notification as read
  const markAsRead = async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );

    try {
      await markDashboardNotificationRead(id);
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const openNotification = async (notification) => {
    const targetUrl = getNotificationTargetUrl(notification);
    await markAsRead(notification.id);
    if (!targetUrl) return;
    onClose?.();
    navigate(targetUrl);
  };

  const handleNotificationKeyDown = (event, notification) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openNotification(notification);
  };

  const deleteNotification = async (id) => {
    try {
      await axiosInstance.delete(`/api/dashboard/notifications/${id}`, {
        withCredentials: true,
      });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const clearAllNotifications = async () => {
    if (notifications.length === 0) return;
    if (!window.confirm('Clear all notifications?')) return;

    try {
      setClearing(true);
      await axiosInstance.delete('/api/dashboard/notifications', {
        withCredentials: true,
      });
      setNotifications([]);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="relative z-50 w-80 h-full bg-[linear-gradient(to_bottom,_#2A2A2A,_#1A1A1A)] p-6 overflow-y-auto custom-scrollbar shadow-xl animate-slideInLeft">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-[#AF9B8A]"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="mb-6 pr-10 flex items-center justify-between gap-3">
          <h2 className="text-2xl font-bold text-white">Notifications</h2>
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={clearAllNotifications}
              disabled={clearing}
              className="text-xs text-[#AF9B8A] underline hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {clearing ? 'Clearing...' : 'Clear all'}
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-white">Loading...</p>
        ) : notifications.length === 0 ? (
          <p className="text-white">No notifications</p>
        ) : (
          <ul className="space-y-4 text-white">
            {notifications.map((notif) => {
              const targetUrl = getNotificationTargetUrl(notif);

              return (
              <li
                key={notif.id}
                role={targetUrl ? 'button' : undefined}
                tabIndex={targetUrl ? 0 : undefined}
                className={`p-4 rounded-lg transition ${
                  targetUrl ? 'cursor-pointer hover:bg-[#AF9B8A]/20 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]' : 'cursor-default'
                } ${notif.read ? 'bg-white/5' : 'bg-white/20'}`}
                onClick={() => openNotification(notif)}
                onKeyDown={(event) => handleNotificationKeyDown(event, notif)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold">{notif.title}</p>
                    <p className="text-sm">{notif.message}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notif.id);
                    }}
                    className="shrink-0 text-red-300 hover:text-red-500"
                    aria-label="Delete notification"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-300 mt-1">
                  {formatDateTime(notif.createdAt, '')}
                </p>
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
